import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Actions from 'actions/Actions';
import BeyondIdentityUtils from 'utils/BeyondIdentity';
import PasskeyService from 'services/PasskeyService';

import FrameworkUtils from 'utils/framework';
import passkeysUtils from 'utils/passkeys';
import { SUCCESS_DISPLAY_TIME_MS } from 'constants/passkeys';
import UI from 'utils/UI';
import userUtils from 'utils/User';

import LanguageLocaleUtils from 'utils/LanguageLocale';
import PasskeyOptinModalEN from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_en_US';
import PasskeyOptinModalFR from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_fr_CA';

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PasskeyOptinModalEN, PasskeyOptinModalFR);

const localization = createStructuredSelector({
    optinCreateHeading: getTextFromResource(getText, 'optinCreateHeading'),
    subheading: getTextFromResource(getText, 'subheading'),
    warningNotification: getTextFromResource(getText, 'warningNotification'),
    termsAndConditions: getTextFromResource(getText, 'termsAndConditions'),
    termsOfUse: getTextFromResource(getText, 'termsOfUse'),
    termsAndConditionsRest: getTextFromResource(getText, 'termsAndConditionsRest'),
    privacyPolicy: getTextFromResource(getText, 'privacyPolicy'),
    maybeLaterButton: getTextFromResource(getText, 'maybeLaterButton'),
    setupButton: getTextFromResource(getText, 'setupButton'),
    createNewPasskey: getTextFromResource(getText, 'createNewPasskey'),
    cancel: getTextFromResource(getText, 'cancel'),
    passkeyCreated: getTextFromResource(getText, 'passkeyCreated'),
    optinCreateAccountHeading: getTextFromResource(getText, 'optinCreateAccountHeading')
});

const fields = createStructuredSelector({ localization });

const functions = (dispatch, ownProps) => {
    const sendPasskeyMetadata = async ({ bindResult, email }) => {
        const deviceName = UI.getBrowserName();
        const firstName = userUtils.getProfileFirstName();
        const lastName = userUtils.getProfileLastName();

        try {
            await PasskeyService.createPasskeyMetaData({
                passkeyId: bindResult?.passkey?.passkeyId,
                identityId: bindResult?.passkey?.identity?.id,
                deviceName,
                email,
                firstName,
                lastName
            });
        } catch (metaErr) {
            Sephora.logger?.warn('Passkey metadata submission failed', {
                error: metaErr?.message || metaErr,
                passkeyId: bindResult?.passkey?.passkeyId,
                identityId: bindResult?.passkey?.identity?.id,
                deviceName,
                email
            });
        }
    };

    return {
        dispatch,
        onDismiss: (maybeLater = false) => {
            if (maybeLater) {
                // Track the "Maybe Later" dismissal with placement-based logic
                passkeysUtils.trackDismissal();
            }

            dispatch(Actions.showPasskeyOptinModal({ isOpen: false }));

            // Complete flow flow after dismissing opt-in
            const successCallback = ownProps.successCallback;

            if (typeof successCallback === 'function') {
                const onDismissed = { dismissed: true };
                successCallback(onDismissed);
            }
        },
        onSetupPasskey: async () => {
            const { email } = ownProps;

            if (!email) {
                return;
            }

            try {
                dispatch(Actions.showInterstice(true));
                const bindingResponse = await PasskeyService.createPasskeyBinding(email);
                const { credential_binding_link: credentialBindingLink } = bindingResponse;

                if (!credentialBindingLink) {
                    throw new Error('Missing credential_binding_link in response');
                }

                const bindResult = await BeyondIdentityUtils.bindPasskey(credentialBindingLink);

                // Best-effort: send device/user metadata to backend; log but do not block UX on failure
                await sendPasskeyMetadata({ bindResult, email });

                dispatch(Actions.showPasskeyOptinModal({ isOpen: false }));

                const successCallback = ownProps.successCallback;

                if (typeof successCallback === 'function') {
                    successCallback(bindResult);
                }
            } catch (error) {
                dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('passkeyErrorTitle'),
                        message: getText('passkeyErrorMessage'),
                        buttonText: getText('passkeyErrorButton')
                    })
                );
                throw error;
            } finally {
                dispatch(Actions.showInterstice(false));
            }
        },
        displayInfoModal: (infoModalOptions = {}) => {
            dispatch(
                Actions.showPasskeysInfoModal({
                    ...infoModalOptions
                })
            );
        },
        onSuccess: (infoModalOptions = {}) => {
            dispatch(Actions.showInfoModal({ ...infoModalOptions, isOpen: true }));
            setTimeout(() => {
                dispatch(Actions.showInfoModal({ isOpen: false }));
            }, SUCCESS_DISPLAY_TIME_MS);
        }
    };
};

const withPasskeyOptinModalProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, withPasskeyOptinModalProps
};
